import './style.scss'
import { Button, Carousel, message, Modal, Popover } from 'antd'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { FC, memo, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as Api from '@/api/home'
import MarketCircle1 from '@/assets/market-circle1.png'
import MarketCircle2 from '@/assets/market-circle2.png'
import MarketCircle3 from '@/assets/market-circle3.png'
import { Play } from '@/assets/svg'
import VideoModal from '@/components/VideoModal'
import { IUserPackage, UserStore } from '@/global-states'
import { eventTracking, Render, urlSource } from '@/libs/util'
import { CaretLeftOutlined, CaretRightOutlined, CheckOutlined, ExclamationCircleOutlined } from '@ant-design/icons'

dayjs.extend(duration)

interface IProps {
  open: boolean
  marketDetail: any
  onCancel?: () => void
}

const MarketModal: FC<IProps> = (props) => {
  const { t: translate } = useTranslation(['avatar_market', 'system'])
  const navigate = useNavigate()
  const { marketDetail, onCancel, open } = props
  const [preview, setPreview] = useState<any>()
  const [market, setMarket] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [videoCurrent, setVideoCurrent] = useState(0)
  const [videoIndex, setVideoIndex] = useState(0)

  const trackData = useMemo(() => {
    return {
      id: marketDetail?.id,
      name: marketDetail?.name
    }
  }, [marketDetail])

  useEffect(() => {
    if (open) {
      setMarket(marketDetail)
    }
  }, [open])

  const playVideo = () => {
    eventTracking('DigitalAvatarVideo', trackData)
    setPreview({ url: market.videos[videoIndex] })
  }

  const onVideoChange = (index: number) => {
    setVideoIndex(index)
  }

  const onClose = () => {
    setVideoIndex(0)
    setMarket(undefined)
    setVideoCurrent(0)
    onCancel?.()
  }

  const buyNow = async () => {
    eventTracking('DigitalAvatarOneQuote', trackData)
    setLoading(true)

    let totalPoint = 0
    try {
      const userPackage: IUserPackage = await UserStore.updateUserPackage()
      totalPoint =
        (userPackage.membership_credits || 0) + (userPackage.permanent_credits || 0) + (userPackage.free_credits || 0)
    } finally {
      setLoading(false)
    }

    Modal.confirm({
      title: translate('avatar_market.points_consumption_confirmation'),
      content: (
        <div>
          <div>
            {translate('avatar_market.your_points_balance')}: {totalPoint}
            {translate('system.points')}
          </div>
          <div>
            {translate('avatar_market.this_consumption')}: {market.price}
            {translate('system.points')}
          </div>
        </div>
      ),
      okText: translate('system.confirm'),
      cancelText: translate('system.cancel'),
      onOk: async () => {
        await Api.buyMerchandises(market.id)
        Modal.warning({
          title: translate('avatar_market.hire_complete'),
          content: (
            <div>
              {translate('avatar_market.congratulations')}
              <a
                style={{ textDecoration: 'underline', padding: '0 2px' }}
                onClick={() => {
                  Modal.destroyAll()
                  navigate('/home')
                }}
              >
                {translate('avatar_market.my_digital_humans')}
              </a>
              {translate('avatar_market.view_page')}
            </div>
          ),
          okText: translate('system.ok'),
          onOk: () => {
            Modal.destroyAll()
            navigate('/home')
          }
        })
      }
    })
  }

  const addCollect = async () => {
    await Api.addBookmarkedCommunityHumans({
      merchandise_id: market.id
    })
    message.success(translate('avatar_market.collect_success'))
    setMarket({
      ...market,
      bookmarked_by_user: true
    })
  }

  const cancelCollect = async () => {
    await Api.cancelBookmarkedCommunityHumansByMerchandiseId(market.id)
    message.success(translate('avatar_market.collect_canceled'))
    setMarket({
      ...market,
      bookmarked_by_user: false
    })
  }

  const toCreate = async () => {
    const res = await Api.addBookmarkedCommunityHumans({
      merchandise_id: market.id
    })
    if (res.group_id) {
      const avatars = await Api.getDigitalHumansByGroup(res.group_id)
      if (avatars?.list?.length) {
        navigate(`/create-video/${avatars.list[0].id}`)
      } else {
        message.warning(translate('avatar_market.no_available_avatar'))
      }
    }
  }

  if (!market) return null

  return (
    <>
      <Modal
        maskClosable={true}
        title={null}
        open={open}
        width={'800px'}
        footer={null}
        onCancel={onClose}
        className="market-modal"
        closeIcon={null}
      >
        <div className="market-modal-content">
          <div className="modal-content-main">
            <div className="left">
              <Render>
                {function LoadImg() {
                  const [imgClass, setImgClass] = useState('')
                  return (
                    <img
                      onClick={playVideo}
                      className={`img ${imgClass}`}
                      src={urlSource(market?.videos?.[videoIndex], 'video')}
                      onLoad={(e: any) => {
                        const w = e.target?.naturalWidth
                        const h = e.target?.naturalHeight
                        if (h > w) {
                          setImgClass('portrait')
                        }
                      }}
                    />
                  )
                }}
              </Render>

              <Play onClick={playVideo} className="play" />
              {market?.type !== 3 && (
                <div className="tag-desc">
                  <Popover
                    content={
                      <div className="common-popover w400" onClick={(e) => e.stopPropagation()}>
                        <div>
                          <strong>{translate('avatar_market.hire_instructions_title')}</strong>
                        </div>
                        <ul>
                          <li>{translate('avatar_market.hire_instructions_point1')}</li>
                          <li>{translate('avatar_market.hire_instructions_point2')}</li>
                        </ul>
                      </div>
                    }
                    placement="bottom"
                  >
                    {translate('avatar_market.hire_instructions')}
                  </Popover>
                </div>
              )}

              <div className="video-list">
                <Carousel
                  infinite={false}
                  arrows={true}
                  prevArrow={<CaretLeftOutlined />}
                  nextArrow={<CaretRightOutlined />}
                  dots={false}
                  afterChange={(current: number) => {
                    if (videoCurrent < current) {
                      eventTracking('DigitalAvataRightClick', trackData)
                    } else {
                      eventTracking('DigitalAvataLeftClick', trackData)
                    }
                    setVideoCurrent(current)
                  }}
                >
                  {market?.videos?.map(
                    (video: any, index: number) =>
                      index % 4 === 0 && (
                        <div className="carousel-videos" key={index}>
                          <div className="video" onClick={onVideoChange.bind(this, index)}>
                            <Render>
                              {function LoadImg() {
                                const [imgClass, setImgClass] = useState('')
                                return (
                                  <img
                                    className={`${imgClass}`}
                                    src={urlSource(video, 'video')}
                                    onLoad={(e: any) => {
                                      const w = e.target?.naturalWidth
                                      const h = e.target?.naturalHeight
                                      if (h > w) {
                                        setImgClass('portrait')
                                      }
                                    }}
                                  />
                                )
                              }}
                            </Render>

                            {videoIndex === index && (
                              <>
                                <CheckOutlined className="check" />
                                <div className="mask"></div>
                              </>
                            )}
                          </div>

                          {market.videos[index + 1] && (
                            <div className="video" onClick={onVideoChange.bind(this, index + 1)}>
                              <Render>
                                {function LoadImg() {
                                  const [imgClass, setImgClass] = useState('')
                                  return (
                                    <img
                                      className={`${imgClass}`}
                                      src={urlSource(market.videos[index + 1], 'video')}
                                      onLoad={(e: any) => {
                                        const w = e.target?.naturalWidth
                                        const h = e.target?.naturalHeight
                                        if (h > w) {
                                          setImgClass('portrait')
                                        }
                                      }}
                                    />
                                  )
                                }}
                              </Render>

                              {videoIndex === index + 1 && (
                                <>
                                  <CheckOutlined className="check" />
                                  <div className="mask"></div>
                                </>
                              )}
                            </div>
                          )}
                          {market.videos[index + 2] && (
                            <div className="video" onClick={onVideoChange.bind(this, index + 2)}>
                              <Render>
                                {function LoadImg() {
                                  const [imgClass, setImgClass] = useState('')
                                  return (
                                    <img
                                      className={`${imgClass}`}
                                      src={urlSource(market.videos[index + 2], 'video')}
                                      onLoad={(e: any) => {
                                        const w = e.target?.naturalWidth
                                        const h = e.target?.naturalHeight
                                        if (h > w) {
                                          setImgClass('portrait')
                                        }
                                      }}
                                    />
                                  )
                                }}
                              </Render>
                              {videoIndex === index + 2 && (
                                <>
                                  <CheckOutlined className="check" />
                                  <div className="mask"></div>
                                </>
                              )}
                            </div>
                          )}

                          {market.videos[index + 3] && (
                            <div className="video" onClick={onVideoChange.bind(this, index + 3)}>
                              <Render>
                                {function LoadImg() {
                                  const [imgClass, setImgClass] = useState('')
                                  return (
                                    <img
                                      className={`${imgClass}`}
                                      src={urlSource(market.videos[index + 3], 'video')}
                                      onLoad={(e: any) => {
                                        const w = e.target?.naturalWidth
                                        const h = e.target?.naturalHeight
                                        if (h > w) {
                                          setImgClass('portrait')
                                        }
                                      }}
                                    />
                                  )
                                }}
                              </Render>
                              {videoIndex === index + 3 && (
                                <>
                                  <CheckOutlined className="check" />
                                  <div className="mask"></div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )
                  )}
                </Carousel>
              </div>
            </div>
            <div className="right">
              <div className="right-center">
                <div className="circle">
                  <img className="circle-1" src={MarketCircle1} />
                  <img className="circle-2" src={MarketCircle2} />
                  <img className="circle-3" src={MarketCircle3} />
                  <div className="img">
                    <img src={market?.covers?.[0]} />
                  </div>
                  <div className="name">{market?.name}</div>
                  <div className="scene">{market?.scene}</div>
                </div>
              </div>
              <div className="right-bottom">
                {market.type === 3 ? (
                  <div className="btns">
                    {market.bookmarked_by_user ? (
                      <Button color="primary" variant="dashed" onClick={cancelCollect}>
                        {translate('avatar_market.cancel_collect')}
                      </Button>
                    ) : (
                      <Button type="default" onClick={addCollect}>
                        {translate('avatar_market.collect')}
                      </Button>
                    )}
                    <Button type="primary" disabled={market.status === 2} onClick={toCreate}>
                      {translate('avatar_market.go_create')}
                    </Button>
                  </div>
                ) : (
                  <div className="btns">
                    {market.status === 2 ? (
                      <Button type="primary" disabled={market.status === 2}>
                        {translate('avatar_market.hired')}
                      </Button>
                    ) : (
                      <Button type="primary" disabled={market.status === 2} onClick={buyNow} loading={loading}>
                        <span>{market.price}</span>
                        {translate('avatar_market.point_hire')}
                      </Button>
                    )}
                  </div>
                )}

                {market?.special_cost && (
                  <Popover
                    content={
                      <div className="common-popover w400" onClick={(e) => e.stopPropagation()}>
                        <div>
                          <strong>{translate('avatar_market.creation_discount_title')}：</strong>
                        </div>
                        <ul>
                          <li>
                            {translate('avatar_market.creation_discount_point1', {
                              points: market.creation_cost_per_second
                            })}
                            <div className="ml10">
                              a.{' '}
                              {translate('avatar_market.creation_discount_point1a', {
                                points: market.creation_cost_per_second
                              })}
                            </div>
                            <div className="ml10">
                              b.{' '}
                              {translate('avatar_market.creation_discount_point1b', {
                                points: market.creation_cost_per_second + 1
                              })}
                            </div>
                          </li>
                          <li>{translate('avatar_market.creation_discount_point2')}</li>
                        </ul>
                      </div>
                    }
                    placement="bottom"
                  >
                    <div className="market-discount" onClick={(e) => e.stopPropagation()}>
                      {translate('avatar_market.creation_discount_label')}
                      <ExclamationCircleOutlined />
                    </div>
                  </Popover>
                )}
              </div>
            </div>
          </div>

          <VideoModal preview={preview} onCancel={() => setPreview(undefined)} />
        </div>
      </Modal>
    </>
  )
}

export default memo(MarketModal)
